import { createTheme } from '@mui/material/styles';
import { LoadingOverlay } from '@/app/shared/components/Grid/overlays/LoadingOverlay';
import { NoResultsOverlay } from '@/app/shared/components/Grid/overlays/NoResultsOverlay';
import { NoRowsOverlay } from '@/app/shared/components/Grid/overlays/NoRowsOverlay';
import type { PaletteColorOptions } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    yellow: Palette['primary'];
    orange: Palette['primary'];
    red: Palette['primary'];
  }

  interface PaletteOptions {
    yellow?: PaletteColorOptions;
    orange?: PaletteColorOptions;
    red?: PaletteColorOptions;
  }

  interface PaletteColor {
    [key: number]: string;
    [key: string]: string;
  }

  interface SimplePaletteColorOptions {
    [key: number]: string;
    [key: string]: string;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    yellow: true;
  }
}

export const extraColor = {
  palette: {
    green: {
      main: '#1a551f',
      light: '#e5f0da',
      dark: '#c1eb98',
    },
    error: {
      primary: '#f88a86',
      secondary: '#f44336cf',
      light: '#e42424',
    },
    blue: {
      light: '#e9f2f7',
      main: '#a3d0e4',
      '500': '#0089fe',
    },
    grey: {
      light: '#efefef',
      main: '#d8d8d8',
      dark: '#8484841c',
      '400': '#0000004d',
      offwhite: '#e9e8ee',
    },
    transparent: {
      light: '#6d6d6d00',
    },
    orange: {
      dark: '#dc6601',
      main: '#f1b047',
      light: '#f5c77e',
      '400': '#ffc107',
    },
  },
};

/* =========================
   BASE THEME
========================= */

export const colorTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#00AA8F',
      light: '#33bba5',
      dark: '#007f6b',
      contrastText: '#ffff',
    },
    secondary: {
      main: '#C3C3C3',
      light: '#F2F2F2',
      dark: '#7B838A',
    },
    info: {
      main: '#3684B0',
      light: '#FFBD54',
      dark: '#E26943',
    },
    error: {
      main: '#d32f2f',
      light: '#f4b8b6',
      200: '#f88a86',
      contrastText: '#ffff',
    },
    success: {
      main: '#67a12f',
      dark: '#578528',
      light: '#e5f0da',
      '600': '#1a551f',
    },
    text: {
      primary: '#000000',
      secondary: '#2D2D2D',
    },
    common: {
      white: '#fff',
      black: '#000',
    },
    grey: {
      400: '#e1e1e1',
      500: '#dddddd',
      700: '#949494',
    },
    yellow: {
      main: '#fabb65',
    },
    orange: {
      main: '#f1b047',
      light: '#f5c77e',
      dark: '#dc6601',
      200: '#FF851B',
      400: '#ffc107',
      50: '#FFF5E6',
    },
    red: {
      main: '#FF0000',
      200: '#FF0000',
    },
  },
  spacing: 8,
  drawerWidth: '260px',
  typography: {
    fontFamily: '"Open Sans", "Roboto", "Helvetica", "Arial", sans-serif',
  },
});

export const muiTheme = createTheme(colorTheme, {
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontSize: '13px',
          lineHeight: '1.3',
          '@page': {
            size: 'auto',
            marginTop: '0.5cm',
          },
        },
        span: {
          fontSize: '13px',
          lineHeight: '1.3',
        },
        div: {
          fontSize: '13px',
          lineHeight: '1.3',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          gap: '10px',
          display: 'flex',
          alignItems: 'center',
          fontSize: 12,
        },
        h1: {
          fontSize: 22,
          fontWeight: 500,
          lineHeight: '20px',
        },
        h2: {
          fontSize: 20,
          fontWeight: 400,
          lineHeight: 1.3,
        },
        h3: {
          fontSize: 18,
          fontWeight: 400,
          marginBottom: '10px',
          lineHeight: 1.3,
        },
        h4: {
          fontSize: 16,
          fontWeight: 400,
        },
        body1: {
          fontSize: 13,
          fontWeight: 400,
        },
        subtitle2: {},
        subtitle1: {
          fontSize: 14,
          fontWeight: 400,
        },
        caption: {
          color: `${colorTheme.palette.common.black}`,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 700,
          minHeight: '30px',
          boxShadow: 'none',
        },
        containedPrimary: {
          backgroundColor: `${colorTheme.palette.primary.light}`,
        },
        containedError: {
          color: `${colorTheme.palette.common.white}`,
        },
        containedInfo: {
          color: `${colorTheme.palette.common.white}`,
          background: `${colorTheme.palette.info.dark}`,
          '&:hover': {
            backgroundColor: `${colorTheme.palette.info.dark}`,
          },
        },
        textInfo: {
          color: `${colorTheme.palette.info.dark}`,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          fontSize: 12,
          fontWeight: 600,
          color: `${colorTheme.palette.primary.main}`,
          borderRadius: 0,
          display: 'flex',
          justifyContent: 'center',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: `${colorTheme.palette.secondary.main}`,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          letterSpacing: '.445px',
          textTransform: 'none',
          '&.Mui-selected': {
            fontWeight: 700,
            letterSpacing: 0,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          border: 'none',
        },
        indicator: {
          height: '4px',
          backgroundColor: `${colorTheme.palette.info.light}`,
          transition: 'all 100ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        },
        scrollButtons: {
          '&.Mui-disabled': {
            display: 'none',
          },
        },
      },
    },
    MuiDataGrid: {
			defaultProps: {
				editMode: 'row',
				pagination: true,
				components: {
					NoRowsOverlay,
					NoResultsOverlay,
					LoadingOverlay,
				},
			},
			styleOverrides: {
				root: {
					border: 'none',
					overflow: 'hidden',
          backgroundColor: `${colorTheme.palette.common.white}`,
				},
				filterForm: {
					gap: 20,
					padding: 20,
					paddingRight: 10,
				},
				filterFormDeleteIcon: {
					order: 1,
					marginLeft: -10,
				},
				row: {
					fontSize: 13,
					cursor: 'pointer',
					'&--editing': {
						boxShadow: 'none',
						'.MuiDataGrid-cell': {
							background: 'transparent',
							minHeight: 38
						},
					},
					'&.Mui-selected, &:hover': {
						'.MuiDataGrid-cell[data-field="options"] > div, .MuiDataGrid-cell:last-child > *': {
							opacity: 1,
							pointerEvents: 'auto',
						},
					},
					'.Mui-selected': {
						background: `${colorTheme.palette.primary.light}`,
					},
				},
				cell: {
					color: `${colorTheme.palette.common.black}`,
					borderColor: `${colorTheme.palette.secondary.main}`,
					'&:focus, &:active, &:focus-within': {
						outline: 'none',
					},
					'&:first-child': {
						paddingLeft: 8,
						'&:not(.MuiDataGrid-cellCheckbox)': {
							padding: '8px 10px 8px 20px',
						},
					},
					'&.MuiDataGrid-cell--editing': {
						boxShadow: 'none',
						padding: '0px 10px 0px 10px',
						background: 'none',
						'.MuiInputBase-root': {
							height: '100%',
							borderRadius: 0
						},
						'.MuiOutlinedInput-root.Mui-error': {
							borderBottom: `1px solid ${colorTheme.palette.error.main}`
						},
						input: {
							textAlign: 'inherit',
							borderRadius: 0,
							border: 'none',
							'&:focus': {
								borderColor: `${colorTheme.palette.primary.main}`,
							},
							'&[type="datetime-local"], &[type="date"]': {
								padding: '4px 4px 4px 10px',
							},
						},
						'&:focus-within': {
							outline: 'none',
						},
						'&:last-child': {
							minWidth: 140,
						},
					},
				},
				editInputCell: {
					fontSize: 'inherit',
					input: {
						padding: '0 10px',
					},
				},
				columnHeaders: {
					backgroundColor: `${colorTheme.palette.secondary.light}`,
				},
				columnHeader: {
					padding: '0 8px',
					'&--sortable': {
						'&:first-child': {
							padding: '0 20px',
						},
					},
					'&:focus-within': {
						outline: 'none',
					},
					'&:last-child': {
						'.MuiDataGrid-columnSeparator': {
							display: 'none',
						},
					},
				},
				columnHeaderTitle: {
					fontWeight: 700,
					lineHeight: '19px',
					whiteSpace: 'normal',
					color: `${colorTheme.palette.common.black}`,
				},
				columnHeaderTitleContainer: {
					maxWidth: '100%',
				},
				checkboxInput: {
					color: `${colorTheme.palette.secondary.main}`,
					'&.Mui-checked': {
						color: `${colorTheme.palette.primary.main}`,
					},
				},
			},
		},
    MuiPopover: {
      defaultProps: {
        elevation: 3,
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          overflow: 'hidden',
          fontSize: 12,
          paddingTop: 0,
          paddingBottom: 0,
          fontWeight: 400,
          border: 0,
          borderBottom: `1px solid ${colorTheme.palette.secondary.main}`,
          borderRadius: 0,
          '&.Mui-disabled': {
            background: `${colorTheme.palette.secondary.main}`,
          },
        },
        input: {
          background: `${colorTheme.palette.common.white}`,
          color: `${colorTheme.palette.primary.main}`,
          borderRadius: 0,
          '&.Mui-disabled': {
            background: 'transparent',
            WebkitTextFillColor: `${colorTheme.palette.primary.main}`,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          bottom: 0,
          margin: 0,
          position: 'absolute',
          transform: 'translateY(100%)',
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          '&.MuiGrid-container': {
            marginBottom: 10,
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          height: '100%',
        },
        label: {
          fontWeight: 100,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: '2px',
        },
      },
    },
  },
});
